import React, { ChangeEvent, ReactElement, useEffect } from 'react';

import {
    DialogContent,
    TextField,
    Grid2,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
} from '@mui/material';
import { ClientType, EntityType, IClientDto } from '../../../api/ClientApi';
import {
    clearClient,
    createNewEmptyCustomer,
    createNewEmptySupplier,
    importClient,
    saveClient,
    updateClientAddress,
    updateClientCity,
    updateClientContactInfo,
    updateClientEmail,
    updateClientEntityType,
    updateClientName,
    updateClientPhone,
    updateClientShortName,
    updateClientState,
    updateClientTaxId,
    updateClientUcin,
} from '../../../redux/actions/clientActions';
import { useAppDispatch } from '../../../hooks/hooks';
import { useSelector } from 'react-redux';
import { getClient } from '../../../redux/selectors/clientsSelector';
import { RootState } from '../../../redux/reducers';
import { SaveEntityStatus } from '../../../redux/reducers/common';
import { ClientTypeSR, EntityTypeSR } from '../../../common/Types';
import { useLocation } from 'react-router-dom';
import { isSupplierPage, isCustomerPage, isSupplier, isCustomer } from '../ClientsHelper';

import { FormActions, FormDialogTitle } from '../../../common/components/form/FormPrimitives';

import { ResponsiveDialog } from '../../../common/components/mobile/MobilePrimitives';

export interface ClientFormProps {
    open: boolean;
    closeForm(): void;
    clientId?: number;
}

export default function ClientForm(props: ClientFormProps): ReactElement {
    const { open, closeForm, clientId } = props;

    const client: IClientDto | null = useSelector(getClient);
    const saveStatus = useSelector((state: RootState) => state.clientForm.saveStatus);

    const dispatch = useAppDispatch();

    const location = useLocation();
    const path = location.pathname;

    const pageClientType = isSupplierPage(path)
        ? ClientType.Supplier
        : isCustomerPage(path)
          ? ClientType.Customer
          : undefined;

    useEffect(() => {
        if (open) {
            if (!!clientId && clientId > 0) {
                dispatch(importClient(clientId));
            } else {
                if (!client) {
                    if (isSupplier(pageClientType)) {
                        dispatch(createNewEmptySupplier());
                    } else if (isCustomer(pageClientType)) {
                        dispatch(createNewEmptyCustomer());
                    }
                }
            }
        }
    }, [dispatch, clientId, pageClientType, open]);

    useEffect(() => {
        if (saveStatus === SaveEntityStatus.Saved) {
            handleClose();
        }
    }, [dispatch, saveStatus]);

    const handleInputElementChange = (event: ChangeEvent<HTMLInputElement>) => {
        const target = event.target;
        const value = target.value;
        const name = target.name;

        switch (name) {
            case 'ucin':
                dispatch(updateClientUcin(value));
                break;
            case 'phone':
                dispatch(updateClientPhone(value));
                break;
            case 'taxId':
                dispatch(updateClientTaxId(value));
                break;
            case 'address':
                dispatch(updateClientAddress(value));
                break;
            case 'city':
                dispatch(updateClientCity(value));
                break;
            case 'state':
                dispatch(updateClientState(value));
                break;
            case 'contactInfo':
                dispatch(updateClientContactInfo(value));
                break;
            case 'shortName':
                dispatch(updateClientShortName(value));
                break;
            case 'name':
                dispatch(updateClientName(value));
                break;
            case 'email':
                dispatch(updateClientEmail(value));
                break;
            default:
                console.log('%cERROR', 'color: red');
        }
    };

    const handleSelectEntityType = (event: SelectChangeEvent<EntityType>) => {
        const value = event.target.value;
        if (value === '') {
            dispatch(updateClientEntityType(undefined));
        } else if (value === EntityType[EntityType.Physical]) {
            dispatch(updateClientEntityType(EntityType.Physical));
        } else if (value === EntityType[EntityType.Legal]) {
            dispatch(updateClientEntityType(EntityType.Legal));
        }
    };

    const handleSave = () => {
        dispatch(saveClient());
    };

    const handleClose = () => {
        dispatch(clearClient());
        closeForm();
    };

    const isClientDataValid =
        !!client &&
        !!client.name &&
        client.name.trim() !== '' &&
        !!client.state &&
        client.state.trim() !== '' &&
        !!client.shortName &&
        client.shortName.trim() !== '' &&
        (client.entityType === EntityType.Physical || (!!client.taxId && client.taxId.trim() !== '')) &&
        !!client.entityType;

    return (
        <ResponsiveDialog scroll="body" maxWidth="md" open={open} onClose={handleClose}>
            <FormDialogTitle>
                {pageClientType ? ClientTypeSR[pageClientType] : ''} - {!clientId ? 'dodavanje' : 'izmena'}
            </FormDialogTitle>
            <DialogContent dividers sx={{ px: { xs: 2, sm: 5 }, pt: { xs: 3, sm: 5 } }}>
                <Grid2 container spacing={2} style={{ maxWidth: '700px', margin: 'auto' }}>
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                        <TextField
                            error={!client?.name || client?.name.trim() === ''}
                            required
                            fullWidth
                            name="name"
                            label="Ime"
                            value={client?.name ?? ''}
                            variant="outlined"
                            onChange={handleInputElementChange}
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                        <TextField
                            error={!client?.shortName || client?.shortName.trim() === ''}
                            required
                            fullWidth
                            name="shortName"
                            label="Skr. ime"
                            value={client?.shortName ?? ''}
                            variant="outlined"
                            onChange={handleInputElementChange}
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                        <TextField
                            error={!client?.state || client.state.trim() === ''}
                            required
                            fullWidth
                            name="state"
                            label="Država"
                            value={client?.state ?? ''}
                            variant="outlined"
                            onChange={handleInputElementChange}
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                        <TextField
                            fullWidth
                            name="city"
                            label="Grad"
                            value={client?.city ?? ''}
                            variant="outlined"
                            onChange={handleInputElementChange}
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                        <TextField
                            fullWidth
                            name="address"
                            label="Adresa"
                            value={client?.address ?? ''}
                            variant="outlined"
                            onChange={handleInputElementChange}
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                        <TextField
                            fullWidth
                            name="email"
                            label="Email"
                            value={client?.email ?? ''}
                            variant="outlined"
                            onChange={handleInputElementChange}
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                        <TextField
                            fullWidth
                            name="phone"
                            label="Telefon"
                            value={client?.phone ?? ''}
                            variant="outlined"
                            onChange={handleInputElementChange}
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                        <FormControl fullWidth error={!client?.entityType} required>
                            <InputLabel id="demo-simple-select-label">Tip klijenta</InputLabel>
                            <Select
                                labelId="demo-simple-select-label"
                                id="demo-simple-select"
                                value={client?.entityType ?? ''}
                                label="Tip klijenta"
                                onChange={handleSelectEntityType}
                            >
                                {Object.values(EntityType).map((et) => {
                                    return (
                                        <MenuItem key={et} value={et as EntityType}>
                                            {EntityTypeSR[et as EntityType]}
                                        </MenuItem>
                                    );
                                })}
                            </Select>
                        </FormControl>
                    </Grid2>
                    {client?.entityType === EntityType.Legal ? (
                        <Grid2 size={{ xs: 12, sm: 6 }}>
                            <TextField
                                error={!client?.taxId || client?.taxId.trim() === ''}
                                required
                                fullWidth
                                name="taxId"
                                label="Jedinstveni broj firme (PIB/JIB...)"
                                value={client?.taxId ?? ''}
                                variant="outlined"
                                onChange={handleInputElementChange}
                            />
                        </Grid2>
                    ) : client?.entityType === EntityType.Physical ? (
                        <Grid2 size={{ xs: 12, sm: 6 }}>
                            <TextField
                                fullWidth
                                name="ucin"
                                label="JMBG"
                                value={client?.ucin ?? ''}
                                variant="outlined"
                                onChange={handleInputElementChange}
                            />
                        </Grid2>
                    ) : (
                        <Grid2 size={{ xs: 12, sm: 6 }} />
                    )}
                    <FormActions
                        onCancel={handleClose}
                        onSubmit={handleSave}
                        submitLabel="Sačuvaj"
                        disabled={!isClientDataValid || saveStatus === SaveEntityStatus.Pending}
                        pending={saveStatus === SaveEntityStatus.Pending}
                    />
                </Grid2>
            </DialogContent>
        </ResponsiveDialog>
    );
}
